import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    MapPin,
    Phone,
    Mail,
    Clock,
    Send,
    CheckCircle2,
    AlertCircle,
} from 'lucide-react';

import { submitInquiry } from 'context/actions/inquiries';
import PageHeader from 'components/PageHeader';

// reCAPTCHA v3 script loader
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const loadRecaptcha = () => {
    if (!RECAPTCHA_SITE_KEY) return;
    if (typeof window === 'undefined') return;
    if (document.querySelector('script[data-recaptcha]')) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-recaptcha', 'true');
    document.head.appendChild(script);
};

const getRecaptchaToken = async () => {
    if (!RECAPTCHA_SITE_KEY || typeof window === 'undefined') return '';
    return new Promise((resolve) => {
        if (!window.grecaptcha) return resolve('');
        window.grecaptcha.ready(() => {
            window.grecaptcha
                .execute(RECAPTCHA_SITE_KEY, { action: 'inquiry_submit' })
                .then(resolve)
                .catch(() => resolve(''));
        });
    });
};

export default function ContactPage() {
    const { t } = useTranslation();
    const lang = useSelector((s) => s.system.lang || 'mn');
    const companyInfo = useSelector((s) => s.system.companyInfo || {});

    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        message: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null
    const [errors, setErrors] = useState({});

    useEffect(() => {
        loadRecaptcha();
    }, []);

    const update = (k, v) => {
        setForm((p) => ({ ...p, [k]: v }));
        if (errors[k]) setErrors((p) => ({ ...p, [k]: null }));
    };

    const reset = () => {
        setForm({ name: '', phone: '', email: '', message: '' });
        setErrors({});
        setStatus(null);
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = t('contact.validation.nameRequired');
        if (!form.message.trim())
            e.message = t('contact.validation.messageRequired');
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            e.email = t('contact.validation.invalidEmail');
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        setStatus(null);

        const recaptchaToken = await getRecaptchaToken();
        const res = await submitInquiry({ ...form, recaptchaToken });

        if (res?.code === 200 || res?.status === 'success' || res?.code === 201) {
            setStatus('success');
            reset();
        } else {
            setStatus('error');
        }
        setSubmitting(false);
    };

    // Map coords
    const lat = parseFloat(companyInfo.map_lat) || 47.918873;
    const lng = parseFloat(companyInfo.map_lng) || 106.917695;

    return (
        <>
            <Head>
                <title>{`${t('contact.title')} — Mongol Refinery`}</title>
            </Head>

            {/* ─── Page hero ─── */}
            <PageHeader slug="contact" title={t('contact.title')} />

            {/* ─── Contact info + Form ─── */}
            <section className="py-16 lg:py-24 bg-brand-cream">
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16">
                        {/* ── Left: contact info ── */}
                        <div>
                            <h2 className="text-2xl lg:text-3xl font-black text-brand-dark mb-8">
                                {lang === 'en' ? 'Get in touch' : 'Бидэнтэй холбогдох'}
                            </h2>
                            <div className="space-y-6">
                                {companyInfo.address && (
                                    <InfoRow
                                        icon={MapPin}
                                        label={t('common.address')}
                                        value={companyInfo.address}
                                    />
                                )}
                                {companyInfo.phone && (
                                    <InfoRow
                                        icon={Phone}
                                        label={t('common.phone')}
                                        value={companyInfo.phone}
                                        href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                                    />
                                )}
                                {companyInfo.email && (
                                    <InfoRow
                                        icon={Mail}
                                        label={t('common.email')}
                                        value={companyInfo.email}
                                        href={`mailto:${companyInfo.email}`}
                                    />
                                )}
                                {companyInfo.working_hours && (
                                    <InfoRow
                                        icon={Clock}
                                        label={t('common.workingHours')}
                                        value={companyInfo.working_hours}
                                    />
                                )}
                            </div>

                            {/* ── Газрын зураг: зүүн текстүүдийн доор ── */}
                            <div className="mt-8 aspect-[4/3] bg-brand-cream-dark overflow-hidden">
                                <iframe
                                    title="Mongol Refinery office location"
                                    src={`https://maps.google.com/maps?q=${lat},${lng}&z=16&output=embed`}
                                    className="w-full h-full border-0"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>

                        {/* ── Right: form ── */}
                        <div className="bg-white p-8 lg:p-10 shadow-lg">
                            <h2
                                className={`text-2xl lg:text-3xl font-black text-brand-dark ${
                                    t('contact.formSubtitle') ? 'mb-2' : 'mb-8'
                                }`}
                            >
                                {t('contact.formTitle')}
                            </h2>
                            {t('contact.formSubtitle') && (
                                <p className="text-sm text-brand-gray mb-8">
                                    {t('contact.formSubtitle')}
                                </p>
                            )}

                            {status === 'success' && (
                                <div className="mb-6 p-4 bg-brand-green/10 border-l-4 border-brand-green flex gap-3">
                                    <CheckCircle2
                                        size={20}
                                        className="text-brand-green flex-shrink-0 mt-0.5"
                                    />
                                    <div>
                                        <p className="font-bold text-brand-dark">
                                            {t('contact.thankYou')}
                                        </p>
                                        <p className="text-sm text-brand-gray mt-1">
                                            {t('contact.thankYouMessage')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {status === 'error' && (
                                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 flex gap-3">
                                    <AlertCircle
                                        size={20}
                                        className="text-red-500 flex-shrink-0 mt-0.5"
                                    />
                                    <p className="text-sm text-brand-dark">
                                        {t('contact.error')}
                                    </p>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <Field
                                        label={t('contact.name') + ' *'}
                                        placeholder={t('contact.namePlaceholder')}
                                        value={form.name}
                                        onChange={(v) => update('name', v)}
                                        error={errors.name}
                                    />
                                    <Field
                                        label={t('contact.phone')}
                                        placeholder={t('contact.phonePlaceholder')}
                                        value={form.phone}
                                        onChange={(v) => update('phone', v)}
                                        type="tel"
                                    />
                                </div>
                                <Field
                                    label={t('contact.email')}
                                    placeholder={t('contact.emailPlaceholder')}
                                    value={form.email}
                                    onChange={(v) => update('email', v)}
                                    type="email"
                                    error={errors.email}
                                />
                                <Field
                                    label={t('contact.message') + ' *'}
                                    placeholder={t('contact.messagePlaceholder')}
                                    value={form.message}
                                    onChange={(v) => update('message', v)}
                                    textarea
                                    error={errors.message}
                                />

                                <div className="flex flex-wrap gap-3 pt-2">
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="inline-flex items-center gap-2 bg-brand-dark text-white font-bold px-8 py-3.5 hover:bg-brand-yellow hover:text-brand-dark transition disabled:opacity-50"
                                    >
                                        {submitting ? (
                                            t('common.loading')
                                        ) : (
                                            <>
                                                <Send size={16} />
                                                {t('contact.send')}
                                            </>
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={reset}
                                        className="inline-flex items-center gap-2 border-2 border-brand-cream-dark text-brand-dark font-bold px-8 py-3.5 hover:border-brand-yellow transition"
                                    >
                                        {t('contact.reset')}
                                    </button>
                                </div>

                                {RECAPTCHA_SITE_KEY && (
                                    <p className="text-xs text-brand-gray mt-4">
                                        {lang === 'en'
                                            ? 'Protected by reCAPTCHA. '
                                            : 'reCAPTCHA-аар хамгаалагдсан. '}
                                        <a
                                            href="https://policies.google.com/privacy"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-brand-yellow-dark hover:underline"
                                        >
                                            Privacy
                                        </a>
                                        {' & '}
                                        <a
                                            href="https://policies.google.com/terms"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-brand-yellow-dark hover:underline"
                                        >
                                            Terms
                                        </a>
                                    </p>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}

// ─── Components ──────────────────────────────────────────────

function InfoRow({ icon: Icon, label, value, href }) {
    const inner = (
        <>
            <div className="flex-shrink-0 w-11 h-11 flex items-center justify-center bg-brand-yellow text-brand-dark">
                <Icon size={18} />
            </div>
            <div>
                <p className="text-xs text-brand-gray uppercase tracking-wider mb-1 font-medium">
                    {label}
                </p>
                <p className="text-brand-dark font-semibold leading-relaxed">
                    {value}
                </p>
            </div>
        </>
    );
    return href ? (
        <a
            href={href}
            className="flex items-start gap-4 hover:text-brand-yellow-dark transition"
        >
            {inner}
        </a>
    ) : (
        <div className="flex items-start gap-4">{inner}</div>
    );
}

function Field({ label, placeholder, value, onChange, error, type = 'text', textarea }) {
    const baseClass =
        'w-full px-4 py-3 bg-white border-2 transition focus:outline-none placeholder:text-brand-gray-light';
    const borderClass = error
        ? 'border-red-400 focus:border-red-500'
        : 'border-brand-cream-dark focus:border-brand-yellow';

    return (
        <div>
            <label className="block text-sm font-bold text-brand-dark mb-2">
                {label}
            </label>
            {textarea ? (
                <textarea
                    rows={5}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${baseClass} ${borderClass} resize-none`}
                />
            ) : (
                <input
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${baseClass} ${borderClass}`}
                />
            )}
            {error && (
                <p className="mt-1 text-xs text-red-500">{error}</p>
            )}
        </div>
    );
}